import { useEffect, useRef } from "react";
import { trackRegistrationStart } from "@/lib/hubspotSignupTracking";

const CSS_ID = "mc-embed-classic-css";
const VALIDATE_ID = "mc-validate-js";

/**
 * Mailchimp Embedded Form (utskickslistan).
 * Laddar Mailchimps klassiska CSS och mc-validate.js en enda gång globalt.
 */
const MailchimpSignupForm = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!document.getElementById(CSS_ID)) {
      const link = document.createElement("link");
      link.id = CSS_ID;
      link.rel = "stylesheet";
      link.href = "https://cdn-images.mailchimp.com/embedcode/classic-061523.css";
      document.head.appendChild(link);
    }

    const w = window as unknown as Record<string, unknown>;
    w.fnames = ["EMAIL", "FNAME", "LNAME", "PHONE", "COMPANY", "CITY"];
    w.ftypes = ["email", "text", "text", "phone", "text", "text"];

    if (!document.getElementById(VALIDATE_ID)) {
      const script = document.createElement("script");
      script.id = VALIDATE_ID;
      script.src = "https://s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js";
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onFocus = () => trackRegistrationStart("form_interaction");
    root.addEventListener("focusin", onFocus);
    return () => {
      root.removeEventListener("focusin", onFocus);
    };
  }, []);

  return (
    <div ref={rootRef} className="mc-embed-wrapper w-full max-w-full overflow-x-hidden">
      <style>{`
        #mc_embed_signup { width: 100%; max-width: 100%; background: transparent; }
        #mc_embed_signup form { padding: 0; }
        #mc_embed_signup .mc-field-group { width: 100%; }
        #mc_embed_signup .mc-field-group input { width: 100%; box-sizing: border-box; }
        #mc_embed_signup p, #mc_embed_signup a { overflow-wrap: anywhere; word-break: break-word; }
        #mc-embedded-subscribe-form input[type=checkbox] { display: inline; width: auto; margin-right: 10px; }
        #mergeRow-gdpr { margin-top: 20px; }
        #mergeRow-gdpr fieldset label { font-weight: normal; }
        #mc-embedded-subscribe-form .mc_fieldset { border: none; min-height: 0; padding-bottom: 0; }
      `}</style>
      <div id="mc_embed_signup">
        <form
          action="https://affarsnatverket.us4.list-manage.com/subscribe/post?u=47432cbcc5110f849888e5ce5&amp;id=3e205eb108&amp;v_id=3090&amp;f_id=002f58e9f0"
          method="post"
          id="mc-embedded-subscribe-form"
          name="mc-embedded-subscribe-form"
          className="validate"
          target="_blank"
        >
          <div id="mc_embed_signup_scroll">
            <h2>Anmäl dig till utskickslistan</h2>
            <div className="indicates-required">
              <span className="asterisk">*</span> obligatoriskt fält
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-EMAIL">
                E-postadress <span className="asterisk">*</span>
              </label>
              <input type="email" name="EMAIL" className="required email" id="mce-EMAIL" required />
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-FNAME">Förnamn</label>
              <input type="text" name="FNAME" className="text" id="mce-FNAME" />
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-LNAME">Efternamn</label>
              <input type="text" name="LNAME" className="text" id="mce-LNAME" />
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-PHONE">Mobilnummer</label>
              <input type="text" name="PHONE" className="REQ_CSS" id="mce-PHONE" />
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-COMPANY">Företag</label>
              <input type="text" name="COMPANY" className="text" id="mce-COMPANY" />
            </div>
            <div className="mc-field-group">
              <label htmlFor="mce-CITY">Stad</label>
              <input type="text" name="CITY" className="text" id="mce-CITY" />
            </div>

            <div id="mergeRow-gdpr" className="mergeRow gdpr-mergeRow content__gdprBlock mc-field-group">
              <div className="content__gdpr">
                <label>Samtycke till e-postutskick</label>
                <p>Välj hur du vill få information från Affärsnätverket:</p>
                <fieldset className="mc_fieldset gdprRequired mc-field-group" name="interestgroup_field">
                  <label className="checkbox subfield" htmlFor="gdpr_48016">
                    <input
                      type="checkbox"
                      id="gdpr_48016"
                      name="gdpr[48016]"
                      value="Y"
                      className="av-checkbox"
                    />
                    <span>
                      E-post om kommande nätverksträffar och information från Affärsnätverket
                    </span>
                  </label>
                </fieldset>
                <p>
                  Du kan när som helst avsluta prenumerationen via länken i våra mejl. Vi behandlar
                  dina personuppgifter enligt vår integritetspolicy. Läs mer på{" "}
                  <a href="https://affarsnatverket.se/integritetsskydd-cookies-och-gdpr">
                    https://affarsnatverket.se/integritetsskydd-cookies-och-gdpr
                  </a>
                </p>
              </div>
              <div className="content__gdprLegal">
                <p>
                  We use Mailchimp as our marketing platform. By clicking below to subscribe, you
                  acknowledge that your information will be transferred to Mailchimp for processing.{" "}
                  <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noreferrer">
                    Learn more
                  </a>{" "}
                  about Mailchimp's privacy practices.
                </p>
              </div>
            </div>

            <div id="mce-responses" className="clear foot">
              <div className="response" id="mce-error-response" style={{ display: "none" }} />
              <div className="response" id="mce-success-response" style={{ display: "none" }} />
            </div>
            <div aria-hidden="true" style={{ position: "absolute", left: "-5000px" }}>
              <input
                type="text"
                name="b_47432cbcc5110f849888e5ce5_3e205eb108"
                tabIndex={-1}
                defaultValue=""
              />
            </div>
            <input type="hidden" name="tags" value="1870210" />
            <div className="optionalParent">
              <div className="clear foot">
                <input
                  type="submit"
                  name="subscribe"
                  id="mc-embedded-subscribe"
                  className="button"
                  value="Anmäl dig"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MailchimpSignupForm;
